import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { usePageContent } from "@/hooks/usePageContent";
import usePageTitle from "@/hooks/usePageTitle";
import axios from "axios";
import { 
  ArrowRight, 
  CheckCircle, 
  XCircle, 
  Lightbulb,
  Users,
  Globe,
  Leaf,
  GraduationCap,
  BookOpen,
  Target,
  Recycle,
  Megaphone,
  MapPin,
  ExternalLink
} from "lucide-react";
import { toast } from "sonner";

const rseQuiz = [
  {
    id: 1,
    question: "Que signifie RSE ?",
    context: "Définition",
    options: [
      { text: "Responsabilité Sociale des Entreprises", correct: true },
      { text: "Ressources Stratégiques Environnementales", correct: false },
      { text: "Régulation des Standards Économiques", correct: false },
      { text: "Réglementation Sociétale Européenne", correct: false }
    ],
    explanation: "La RSE désigne l'intégration volontaire des préoccupations sociales et environnementales."
  },
  {
    id: 2,
    question: "Quels sont les 3 piliers du développement durable intégrés dans la RSE ?",
    context: "Fondamentaux",
    options: [
      { text: "Profit, Production, Performance", correct: false },
      { text: "Économique, Social, Environnemental", correct: true },
      { text: "Innovation, Qualité, Service", correct: false },
      { text: "Local, National, International", correct: false }
    ],
    explanation: "La RSE repose sur l'équilibre entre performance économique, responsabilité sociale et environnement."
  },
  {
    id: 3,
    question: "Qu'est-ce que la CSRD ?",
    context: "Réglementation",
    options: [
      { text: "Une certification volontaire", correct: false },
      { text: "Une directive européenne sur le reporting extra-financier", correct: true },
      { text: "Un label de qualité environnementale", correct: false },
      { text: "Un programme de subventions", correct: false }
    ],
    explanation: "La CSRD renforce les obligations de reporting sur la durabilité pour les entreprises européennes."
  },
  {
    id: 4,
    question: "Qu'est-ce qu'une partie prenante (stakeholder) ?",
    context: "Gouvernance",
    options: [
      { text: "Uniquement les actionnaires", correct: false },
      { text: "Tout individu ou groupe impacté par ou pouvant impacter l'entreprise", correct: true },
      { text: "Les clients uniquement", correct: false },
      { text: "Les concurrents", correct: false }
    ],
    explanation: "Les parties prenantes incluent employés, clients, fournisseurs, communautés, actionnaires, ONG..."
  },
  {
    id: 5,
    question: "Qu'est-ce qu'une entreprise à mission ?",
    context: "Modèles d'entreprise",
    options: [
      { text: "Une entreprise à but non lucratif", correct: false },
      { text: "Une entreprise qui inscrit des objectifs sociaux et environnementaux dans ses statuts", correct: true },
      { text: "Une entreprise publique", correct: false },
      { text: "Une start-up technologique", correct: false }
    ],
    explanation: "Le statut d'entreprise à mission (loi PACTE) permet d'inscrire des objectifs extra-financiers."
  },
  {
    id: 6,
    question: "Qu'est-ce que la double matérialité ?",
    context: "Reporting",
    options: [
      { text: "Utiliser deux types de matériaux recyclés", correct: false },
      { text: "Analyser l'impact de l'environnement sur l'entreprise ET inversement", correct: true },
      { text: "Doubler ses investissements durables", correct: false },
      { text: "Avoir deux certifications", correct: false }
    ],
    explanation: "La double matérialité considère les risques pour l'entreprise ET ses impacts sur l'environnement."
  }
];

const StrategieRSE = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const { getField } = usePageContent("rse");
  usePageTitle("Domaines d'Intervention");
  const [quizStarted, setQuizStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);

  const handleStartQuiz = () => {
    setQuizStarted(true);
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setScore(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
  };

  const handleAnswer = (option, index) => {
    if (selectedAnswer !== null) return;
    setSelectedAnswer(index);
    setShowExplanation(true);
    const newAnswers = {
      ...answers,
      [currentQuestion]: { ...option, questionId: rseQuiz[currentQuestion].id }
    };
    setAnswers(newAnswers);
    if (option.correct) setScore(prev => prev + 1);
  };

  const handleNext = () => {
    if (currentQuestion < rseQuiz.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setShowResults(true);
      if (isAuthenticated) saveQuizResult();
    }
  };

  const saveQuizResult = async () => {
    try {
      await axios.post(`${API}/quiz/result`, {
        quiz_type: "rse",
        score: score + (selectedAnswer !== null && rseQuiz[currentQuestion].options[selectedAnswer].correct ? 1 : 0),
        max_score: rseQuiz.length,
        answers
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Résultats sauvegardés !");
    } catch (error) {
      console.error("Failed to save results:", error);
    }
  };

  const progress = ((currentQuestion + 1) / rseQuiz.length) * 100;
  const question = rseQuiz[currentQuestion];
  const percentage = Math.round((score / rseQuiz.length) * 100);

  return (
    <div className="min-h-screen bg-[var(--bone)]" data-testid="strategie-rse-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="blob-bg w-[500px] h-[500px] bg-[var(--dusty-rose)] top-0 left-0"></div>
        <div className="blob-bg w-[400px] h-[400px] bg-[var(--maquis)]/15 bottom-0 right-0"></div>

        <div className="container-premium relative z-10">
          <div className="max-w-3xl">
            <h1 className="font-display text-5xl md:text-6xl font-bold text-[var(--charcoal)] mb-6 leading-tight">
              Domaines <span className="text-[var(--terracotta)] emphasis">d'intervention</span>
            </h1>
            <p className="text-xl text-[var(--stone)] mb-10 leading-relaxed">
              Former, transformer les métiers et engager la montée en compétences dans l'enseignement supérieur et les centres de formation en Corse.
            </p>
          </div>
        </div>
      </section>

      {/* À qui s'adresse cette rubrique ? */}
      {!quizStarted && !showResults && (
        <>
          <section className="pb-20" data-testid="audience-section">
            <div className="container-premium">
              <div className="max-w-4xl mx-auto">
                <h2 className="font-display text-3xl md:text-4xl font-bold text-[var(--charcoal)] mb-8">
                  À qui s'adresse <span className="emphasis">cette rubrique</span> ?
                </h2>

                <p className="text-lg text-[var(--stone)] leading-relaxed mb-8">
                  Cette rubrique s'adresse directement aux établissements de l'enseignement supérieur (universités, écoles spécialisées, IUT) et aux centres de formation professionnelle / CFA.
                </p>

                <p className="text-lg text-[var(--stone)] leading-relaxed mb-8">
                  Elle répond aux besoins de deux publics majeurs :
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-12">
                  {/* Public 1 */}
                  <div className="card-premium p-8" data-testid="audience-students">
                    <div className="w-12 h-12 bg-[var(--terracotta)]/10 rounded-xl flex items-center justify-center mb-5">
                      <GraduationCap className="w-6 h-6 text-[var(--terracotta)]" />
                    </div>
                    <h3 className="font-display text-xl font-bold text-[var(--charcoal)] mb-3">
                      Les étudiants et apprenants
                    </h3>
                    <p className="text-[var(--stone)] leading-relaxed">
                      Pour les former aux enjeux de durabilité et adapter leurs compétences aux métiers de demain qui se construisent aujourd'hui.
                    </p>
                  </div>

                  {/* Public 2 */}
                  <div className="card-premium p-8" data-testid="audience-educators">
                    <div className="w-12 h-12 bg-[var(--maquis)]/10 rounded-xl flex items-center justify-center mb-5">
                      <BookOpen className="w-6 h-6 text-[var(--maquis)]" />
                    </div>
                    <h3 className="font-display text-xl font-bold text-[var(--charcoal)] mb-3">
                      Les équipes éducatives et professionnels adultes
                    </h3>
                    <p className="text-[var(--stone)] leading-relaxed">
                      Enseignants, formateurs, responsables pédagogiques, encadrants et professionnels adultes : pour vous accompagner dans l'appropriation des enjeux environnementaux, vous donner des outils clés en main et engager votre montée en compétences afin de faire évoluer les pratiques et contenus de formation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 3 Piliers Stratégiques */}
          <section className="pb-20 bg-[var(--sand)]" data-testid="pillars-section">
            <div className="container-premium py-20">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-16">
                  <h2 className="font-display text-3xl md:text-4xl font-bold text-[var(--charcoal)]">
                    3 piliers <span className="emphasis">stratégiques</span>
                  </h2>
                  <p className="text-lg text-[var(--stone)] mt-4 max-w-2xl mx-auto">
                    J'articule mes formations et accompagnements sur mesure autour de 3 piliers stratégiques :
                  </p>
                </div>

                {/* Pilier 1: DD & Stratégie Climat */}
                <div className="card-premium p-8 md:p-10 mb-8" data-testid="pillar-climat">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 bg-[var(--maquis)]/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Globe className="w-7 h-7 text-[var(--maquis)]" />
                    </div>
                    <div>
                      <span className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--maquis)]">Pilier 1</span>
                      <h3 className="font-display text-2xl font-bold text-[var(--charcoal)] mt-1">
                        Développement Durable & Stratégie Climat
                      </h3>
                    </div>
                  </div>

                  <div className="space-y-4 ml-0 md:ml-[4.5rem]">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[var(--maquis)] mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-[var(--charcoal)]">Comprendre le cadre global et les limites planétaires :</span>
                        <span className="text-[var(--stone)]"> Assimiler les 7 limites planétaires, les enjeux climatiques actuels et la Stratégie Nationale Bas Carbone (SNBC).</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[var(--maquis)] mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-[var(--charcoal)]">Intégrer les Objectifs de Développement Durable (ODD) :</span>
                        <span className="text-[var(--stone)]"> Utiliser l'Agenda 2030 de la Feuille de route ODD France comme référentiel pédagogique et stratégique pour faire évoluer les cursus et la posture professionnelle.</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[var(--maquis)] mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-[var(--charcoal)]">Décarbonation et trajectoires de transition :</span>
                        <span className="text-[var(--stone)]"> Appréhender les méthodologies de comptabilité carbone et construire des plans d'action de transition opérationnels.</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 ml-0 md:ml-[4.5rem]">
                    <Link
                      to="/strategie-climat"
                      className="inline-flex items-center gap-2 bg-[var(--maquis)] text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-[var(--maquis-dark)] transition-colors"
                      data-testid="pillar1-quiz-link"
                    >
                      Tester mes connaissances
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>

                {/* Pilier 2: Économie Circulaire & Éco-conception */}
                <div className="card-premium p-8 md:p-10 mb-8" data-testid="pillar-circular">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 bg-[var(--terracotta)]/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Recycle className="w-7 h-7 text-[var(--terracotta)]" />
                    </div>
                    <div>
                      <span className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--terracotta)]">Pilier 2</span>
                      <h3 className="font-display text-2xl font-bold text-[var(--charcoal)] mt-1">
                        Économie Circulaire & Éco-conception
                      </h3>
                    </div>
                  </div>

                  <div className="space-y-4 ml-0 md:ml-[4.5rem]">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[var(--terracotta)] mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-[var(--charcoal)]">Pensée systémique :</span>
                        <span className="text-[var(--stone)]"> Analyser les chaînes de valeur globales et cartographier les écosystèmes d'acteurs locaux.</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[var(--terracotta)] mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-[var(--charcoal)]">Redéfinir les modèles économiques :</span>
                        <span className="text-[var(--stone)]"> Passer de la logique linéaire (extraire, fabriquer, jeter) à des modèles circulaires, résilients et régénératifs adaptés au territoire corse.</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[var(--terracotta)] mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-[var(--charcoal)]">S'inspirer du vivant :</span>
                        <span className="text-[var(--stone)]"> Utiliser le biomimétisme comme levier d'innovation soutenable pour transformer les métiers de demain.</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 ml-0 md:ml-[4.5rem] flex flex-wrap gap-3">
                    <Link
                      to="/design-durable"
                      className="inline-flex items-center gap-2 bg-[var(--terracotta)] text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-[var(--terracotta-dark)] transition-colors"
                      data-testid="pillar2-circular-quiz-link"
                    >
                      Tester mes connaissances — Économie circulaire
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link
                      to="/innovation-responsable"
                      className="inline-flex items-center gap-2 border-2 border-[var(--terracotta)] text-[var(--terracotta)] px-6 py-3 rounded-full text-sm font-medium hover:bg-[var(--terracotta)] hover:text-white transition-colors"
                      data-testid="pillar2-biomimicry-quiz-link"
                    >
                      Exemples inspirants — Biomimétisme
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                  </div>
                </div>

                {/* Pilier 3: Marketing Responsable & RSE */}
                <div className="card-premium p-8 md:p-10 mb-8" data-testid="pillar-rse">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 bg-[var(--dusty-rose)]/30 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Megaphone className="w-7 h-7 text-[var(--terracotta-dark)]" />
                    </div>
                    <div>
                      <span className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--terracotta-dark)]">Pilier 3</span>
                      <h3 className="font-display text-2xl font-bold text-[var(--charcoal)] mt-1">
                        Marketing Responsable & RSE
                      </h3>
                    </div>
                  </div>

                  <div className="space-y-4 ml-0 md:ml-[4.5rem]">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[var(--terracotta-dark)] mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-[var(--charcoal)]">Lutter contre le greenwashing :</span>
                        <span className="text-[var(--stone)]"> Adopter une communication transparente, éthique et fondée sur des preuves scientifiques.</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[var(--terracotta-dark)] mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-[var(--charcoal)]">Pivoter vers des offres à impact positif :</span>
                        <span className="text-[var(--stone)]"> Inscrire la Responsabilité Sociétale des Entreprises (RSE) au cœur de la raison d'être des organisations et concevoir des offres respectueuses du vivant.</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 ml-0 md:ml-[4.5rem]">
                    <button
                      onClick={handleStartQuiz}
                      className="inline-flex items-center gap-2 bg-[var(--charcoal)] text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-[var(--charcoal)]/90 transition-colors"
                      data-testid="start-rse-quiz-btn"
                    >
                      Évaluer mes connaissances RSE
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Note on-site */}
                <div className="card-premium p-8 bg-[var(--bone)] border-l-4 border-[var(--terracotta)]" data-testid="onsite-note">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-[var(--terracotta)] mt-0.5 flex-shrink-0" />
                    <p className="text-[var(--stone)] leading-relaxed">
                      Toutes mes interventions de formation et d'accompagnement sont réalisées <strong className="text-[var(--charcoal)]">sur mesure</strong> et dispensées directement au sein de vos établissements et centres de formation sur l'ensemble du <strong className="text-[var(--charcoal)]">territoire corse</strong>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="section-premium relative overflow-hidden">
            <div className="blob-bg w-[500px] h-[500px] bg-[var(--terracotta)]/20 top-0 right-0"></div>
            <div className="container-premium relative z-10 text-center">
              <h2 className="font-display text-4xl md:text-5xl font-bold text-[var(--charcoal)] mb-8">
                Prêt à transmettre<br />
                <span className="text-[var(--terracotta)] emphasis">ensemble</span> ?
              </h2>
              <p className="text-[var(--stone)] text-xl max-w-2xl mx-auto mb-12">
                Faisons le point ensemble sur vos enjeux et construisons un parcours adapté à votre contexte et vos attentes.
              </p>
              <Link to="/contact">
                <button className="btn-primary text-lg px-10 py-5">
                  Prendre rendez-vous
                </button>
              </Link>
            </div>
          </section>
        </>
      )}

      {/* Quiz Section (RSE) */}
      {quizStarted && !showResults && (
        <section className="pb-20">
          <div className="container-premium">
            <div className="max-w-3xl mx-auto">
              <div className="glass-warm rounded-2xl p-6 mb-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-[var(--stone)]">
                    Question {currentQuestion + 1} / {rseQuiz.length}
                  </span>
                  <span className="text-sm font-medium text-[var(--terracotta)]">
                    Score: {score}
                  </span>
                </div>
                <div className="h-2 bg-[var(--sand)] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[var(--terracotta)] rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <div className="card-premium overflow-hidden" data-testid="rse-quiz-card">
                <div className="p-8 md:p-10">
                  <div className="mb-8">
                    <span className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--terracotta)]">
                      {question.context}
                    </span>
                    <h3 className="font-display text-2xl font-semibold text-[var(--charcoal)] mt-2">
                      {question.question}
                    </h3>
                  </div>

                  <div className="space-y-3">
                    {question.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleAnswer(option, index)}
                        disabled={selectedAnswer !== null}
                        data-testid={`rse-option-${index}`}
                        className={`w-full text-left quiz-option-premium flex items-center gap-4 ${
                          selectedAnswer === index 
                            ? option.correct ? 'correct' : 'incorrect'
                            : selectedAnswer !== null && option.correct ? 'correct' : ''
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          selectedAnswer !== null
                            ? option.correct 
                              ? 'bg-[var(--maquis)]/10 text-[var(--maquis)]' 
                              : selectedAnswer === index 
                                ? 'bg-[var(--terracotta)]/10 text-[var(--terracotta)]'
                                : 'bg-[var(--sand)]'
                            : 'bg-[var(--sand)]'
                        }`}>
                          {selectedAnswer !== null ? (
                            option.correct ? <CheckCircle className="w-5 h-5" /> : 
                            selectedAnswer === index ? <XCircle className="w-5 h-5" /> : 
                            <span className="text-sm font-medium">{String.fromCharCode(65 + index)}</span>
                          ) : (
                            <span className="text-sm font-medium text-[var(--stone)]">{String.fromCharCode(65 + index)}</span>
                          )}
                        </div>
                        <span className="flex-1 text-[var(--charcoal)]">{option.text}</span>
                      </button>
                    ))}
                  </div>

                  {showExplanation && (
                    <div className="mt-8 p-6 bg-[var(--sand)] rounded-2xl animate-fade-in">
                      <div className="flex items-start gap-3">
                        <Lightbulb className="w-5 h-5 text-[var(--terracotta)] mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-semibold text-[var(--charcoal)]">Explication : </span>
                          <span className="text-[var(--stone)]">{question.explanation}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {selectedAnswer !== null && (
                  <div className="px-8 md:px-10 pb-8 md:pb-10">
                    <button 
                      onClick={handleNext}
                      className="btn-primary w-full flex items-center justify-center gap-2"
                      data-testid="rse-next-btn"
                    >
                      {currentQuestion < rseQuiz.length - 1 ? "Question suivante" : "Voir les résultats"}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Results */}
      {showResults && (
        <section className="pb-20">
          <div className="container-premium">
            <div className="max-w-2xl mx-auto card-premium overflow-hidden" data-testid="rse-results">
              <div className={`p-10 text-center ${
                percentage >= 70 ? 'bg-[var(--maquis)]/10' : 'bg-[var(--terracotta)]/10'
              }`}>
                <div className="w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6 bg-[var(--bone)]">
                  <Target className={`w-12 h-12 ${
                    percentage >= 70 ? 'text-[var(--maquis)]' : 'text-[var(--terracotta)]'
                  }`} />
                </div>
                <h2 className="font-display text-3xl font-bold text-[var(--charcoal)] mb-2">
                  {percentage >= 70 ? "Expert RSE !" : percentage >= 40 ? "Bonne progression !" : "Découverte en cours !"}
                </h2>
                <p className="text-[var(--stone)]">
                  Vous avez obtenu {score} / {rseQuiz.length} ({percentage}%)
                </p>
              </div>

              <div className="p-10">
                <p className="text-[var(--stone)] text-center mb-8">
                  Au-delà de la conformité, la RSE est une opportunité de créer de la valeur partagée 
                  et construire une marque régénérative.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button onClick={handleStartQuiz} className="btn-secondary flex-1">
                    Refaire le quiz
                  </button>
                  <button 
                    onClick={() => navigate('/contact')}
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                  >
                    Me faire accompagner
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                {!isAuthenticated && (
                  <p className="text-center text-sm text-[var(--stone)] mt-6">
                    <a href="/auth" className="text-[var(--terracotta)] font-medium hover:underline">
                      Créez votre espace
                    </a> pour sauvegarder vos résultats
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default StrategieRSE;
